import { theme as antdTheme } from "antd";
import { fillMessagePlaceholders } from '../messaging/placeholderFill';
import { appThemeConfig } from "../theme/AppTheme";
import { useAppearance } from "../contexts/AppearanceContext";
import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import axios from "axios";
import {
  CopyOutlined, CheckOutlined, EyeOutlined, EyeInvisibleOutlined,
  FilePdfOutlined, FileWordOutlined, FileExcelOutlined,
  FileZipOutlined, FileTextOutlined, FileOutlined,
} from "@ant-design/icons";
import { API_DOMAIN, country_codes, countryCodeFromBsuid } from "../constants";
import { getCountryCode } from "../misc_functions";
import { subscribeAgentEligibility } from "../messaging/agentEligibility";
import { fetchSrmUsers } from "./models/shared/agentsUsersModel";
import { DROPDOWN_VALUES_CHANGED, isOptionActive } from "./models/shared/dropdownValuesModel";
import { WATI_FILE_RULES } from "./constants";
import { registerSessionReset, sessionIsCurrent, sessionTicket } from '../../utils/sessionLifecycle';

// ── Real viewport height, ResizeObserver-measured ─────────────────────────
// For capping dropdown/popup maxHeight. Prefer this over a raw 100vh calc —
// on mobile, browser chrome (address bar) showing/hiding changes the
// *visible* height without firing a window resize event that a vh unit
// would need to recompute; document.documentElement's rendered box does
// reflect that change, and ResizeObserver catches it.
export function useViewportHeight() {
  const [height, setHeight] = useState(() => document.documentElement.clientHeight);
  useEffect(() => {
    const el = document.documentElement;
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect?.height;
      if (h) setHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return height;
}

// ── Dropdown maxHeight that never overflows the screen ────────────────────
// Capping maxHeight at the full viewport height isn't enough on its own — a
// popup that opens low on the page still runs off the bottom even though
// its own height is "only" as tall as the viewport, because it starts
// partway down. Measuring the *popup's* rendered position doesn't work
// either: rc-trigger positions the popup and our height clamp both settle
// during the same mount, racing each other.
//
// The trigger element, on the other hand, is on screen the whole time —
// so this watches *it* live (ResizeObserver on the trigger + the viewport,
// plus scroll/resize listeners since ResizeObserver only reports size
// changes, not the position shifts scrolling causes) and keeps a running
// "space below/above the trigger" figure, the same equation rc-trigger
// itself would use to decide whether to flip placement. `measure()` is
// also exposed to call the instant a dropdown opens, for a guaranteed-fresh
// value with zero race against the popup's own mount.
export function useDropdownMaxHeight(margin = 12, minHeight = 160) {
  const triggerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(400);

  const measure = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect?.();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    // Mirrors rc-trigger's own flip heuristic: use whichever side actually
    // has room, preferring below when both would fit.
    const available = spaceBelow >= minHeight || spaceBelow >= spaceAbove ? spaceBelow : spaceAbove;
    setMaxHeight(Math.max(minHeight, available));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [margin, minHeight]);

  useEffect(() => {
    const el = triggerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    ro.observe(document.documentElement);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    measure();
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure]);

  return { triggerRef, maxHeight, measure };
}

// ── uzd_misc dropdown options (query_source, part_condition_origin, …) ───────
const _miscOptionsCache = {};
export async function fetchMiscOptions(mode) {
  const ticket = sessionTicket();
  if (_miscOptionsCache[mode]) return _miscOptionsCache[mode];
  try {
    const res = await axios.post(
      `${API_DOMAIN}/whatsapp/get_misc_mode`,
      { mode },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    if (!sessionIsCurrent(ticket)) return [];
    if (list.length > 0) {
      _miscOptionsCache[mode] = list.map(o => ({ value: o.value, label: o.label, disabled: !isOptionActive(o) }));
      return _miscOptionsCache[mode];
    }
  } catch { }
  return [];
}

// ── Part condition labels (numeric code → display text) ──────────────────────
// condition_preference is stored as a numeric code (part_condition_origin mode).
// loadConditionLabels() primes the map; conditionLabel() is a sync lookup that
// falls back to the raw value so text values (old payload snapshots) pass through.
let _conditionLabelMap = null;
export async function loadConditionLabels() {
  const ticket = sessionTicket();
  if (_conditionLabelMap) return _conditionLabelMap;
  const list = await fetchMiscOptions("part_condition_origin");
  if (sessionIsCurrent(ticket) && list.length > 0) {
    _conditionLabelMap = Object.fromEntries(list.map(o => [String(o.value), o.label]));
  }
  return _conditionLabelMap ?? {};
}
export function conditionLabel(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  return _conditionLabelMap?.[String(raw)] ?? String(raw);
}

// ── Part side labels (coded value → display text), same pattern as condition —
// loadSideLabels() primes the map; sideLabel() is a sync lookup that falls
// back to the raw value so old free-text values (pre-dropdown) pass through.
let _sideLabelMap = null;
window.addEventListener(DROPDOWN_VALUES_CHANGED, event => {
  delete _miscOptionsCache[event.detail?.mode];
  if (event.detail?.mode === "part_side") _sideLabelMap = null;
  if (event.detail?.mode === "part_condition_origin") _conditionLabelMap = null;
});
export async function loadSideLabels() {
  const ticket = sessionTicket();
  if (_sideLabelMap) return _sideLabelMap;
  const list = await fetchMiscOptions("part_side");
  if (sessionIsCurrent(ticket) && list.length > 0) {
    _sideLabelMap = Object.fromEntries(list.map(o => [String(o.value), o.label]));
  }
  return _sideLabelMap ?? {};
}
export function sideLabel(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  return _sideLabelMap?.[String(raw)] ?? String(raw);
}

// ── Vehicle technical field labels (coded value → display text) ──────────────
// Unlike part_side/part_condition_origin, these three fields aren't
// uzd_misc-backed — CustomerEditDrawer.js's vehicle sub-form hardcodes their
// option lists directly (fixed, small enums that don't need to be
// admin-editable), so the label maps here are static too, mirroring those
// exact options. Sync manually if that form's option lists ever change.
const ENGINE_LAYOUT_LABELS = {
  "inline-3": "Inline-3", "inline-4": "Inline-4", "inline-6": "Inline-6",
  "v4": "V4", "v6": "V6", "v8": "V8", "v10": "V10", "v12": "V12",
  "flat-4": "Flat-4", "flat-6": "Flat-6",
  "w12": "W12", "w16": "W16",
  "rotary": "Rotary", "electric": "Electric Motor",
};
const TRANSMISSION_TYPE_LABELS = {
  "automatic": "Automatic", "manual": "Manual", "cvt": "CVT",
  "dct": "DCT (Dual Clutch)", "amt": "AMT", "semi-automatic": "Semi-Automatic",
};
const DRIVETRAIN_LABELS = {
  "fwd": "FWD", "rwd": "RWD", "awd": "AWD", "4wd": "4WD",
};
export function engineLayoutLabel(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  return ENGINE_LAYOUT_LABELS[String(raw)] ?? String(raw);
}
export function transmissionTypeLabel(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  return TRANSMISSION_TYPE_LABELS[String(raw)] ?? String(raw);
}
export function drivetrainLabel(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  return DRIVETRAIN_LABELS[String(raw)] ?? String(raw);
}

// Shared customer/operator/query fields, and a
// {{#parts}}...{{/parts}} block repeated once per part (only {{part_name}}
// inside — no pricing, unlike the quote-share template).
export function injectUnavailablePartsTemplate(templateText, context) {
  return fillMessagePlaceholders(templateText, context);
}

// Billing details retains its existing misc setting until billing bindings exist.
let _billingDetailsQuickReplyCache = null;
export async function fetchBillingDetailsQuickReply() {
  const ticket = sessionTicket();
  if (_billingDetailsQuickReplyCache) return _billingDetailsQuickReplyCache;
  try {
    const res = await axios.post(
      `${API_DOMAIN}/whatsapp/get_misc_mode`,
      { mode: "billing_details_quick_reply" },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    const data = res.data?.data ?? res.data;
    if (sessionIsCurrent(ticket) && data?.text) {
      _billingDetailsQuickReplyCache = data;
      return data;
    }
  } catch { }
  return null;
}

// Billing text uses the same available customer/operator/query context.
export function injectBillingDetailsTemplate(templateText, context) {
  return fillMessagePlaceholders(templateText, context);
}

// ── WATI channel assignments — which physical WhatsApp number/channel each
// chat UI represents. Stored as an explicit assignment (not derived from
// WATI's raw channel list) so it stays correct regardless of how many
// channels the WATI account has configured — see uzd_misc mode
// 'wati_channel_assignments', e.g. { customer_support: null, supplier_support: "68d2..." }.
let _channelAssignmentsCache = null;
export async function fetchChannelAssignments() {
  const ticket = sessionTicket();
  if (_channelAssignmentsCache) return _channelAssignmentsCache;
  try {
    const res = await axios.post(
      `${API_DOMAIN}/whatsapp/get_misc_mode`,
      { mode: "wati_channel_assignments" },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    const data = res.data?.data ?? res.data;
    if (sessionIsCurrent(ticket) && data && typeof data === "object") {
      _channelAssignmentsCache = data;
      return data;
    }
  } catch { }
  return { customer_support: null, supplier_support: null };
}

// ── Region options (country spec dropdown) — from the `region` table ─────────
// Saved as the region TEXT (display-ready), never the id.
let _regionOptionsCache = null;
export async function fetchRegionOptions() {
  const ticket = sessionTicket();
  if (_regionOptionsCache) return _regionOptionsCache;
  try {
    const res = await axios.post(
      `${API_DOMAIN}/whatsapp/get_region`,
      {},
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    const list = res.data?.success ? (res.data.data ?? []) : [];
    if (!sessionIsCurrent(ticket)) return [];
    if (list.length > 0) {
      _regionOptionsCache = list.map(r => ({ value: r.region, label: r.region }));
      return _regionOptionsCache;
    }
  } catch { }
  return [];
}

// ── Fuel type options — from the fuel types table, saved as TEXT not id ──────
let _fuelTypeOptionsCache = null;
export async function fetchFuelTypeOptions() {
  const ticket = sessionTicket();
  if (_fuelTypeOptionsCache) return _fuelTypeOptionsCache;
  try {
    const res = await axios.post(
      `${API_DOMAIN}/whatsapp/get_fueltypes`,
      {},
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    const list = res.data?.success ? (res.data.data ?? []) : (Array.isArray(res.data) ? res.data : []);
    if (!sessionIsCurrent(ticket)) return [];
    if (list.length > 0) {
      _fuelTypeOptionsCache = list.map(r => ({ value: r.name, label: r.name }));
      return _fuelTypeOptionsCache;
    }
  } catch { }
  return [];
}

// ── Agent fetch cache ─────────────────────────────────────────────────────────
let _agentsCache = null;
let _agentsFetchPromise = null;
let _agentsRevision = 0;
subscribeAgentEligibility(() => { _agentsRevision += 1; _agentsCache = null; _agentsFetchPromise = null; });
// force:true bypasses both the cache and the in-flight dedup — for callers
// that specifically need a live read (e.g. the WATI presence diagnostic
// dropdown in DrawerNew.js), not the last-cached snapshot every other
// agent picker on the page is fine reusing. Still updates the shared cache
// once it resolves, so subsequent non-forced calls benefit from the fresh
// data too.
export function _fetchAgents(force = false) {
  const ticket = sessionTicket();
  if (!force) {
    if (_agentsCache) return Promise.resolve(_agentsCache);
    if (_agentsFetchPromise) return _agentsFetchPromise;
  }
  const revision = ++_agentsRevision;
  const fetchPromise = axios.post(
    API_DOMAIN + "/wati_messages/get_all_agents",
    {},
    { headers: { accessToken: localStorage.getItem("accessToken") } }
  ).then(res => {
    if (!sessionIsCurrent(ticket)) return [];
    if (res.data?.success && revision === _agentsRevision) {
      // Response is { data: { all_agents: [...], online_count } } — the
      // agent array moved one level deeper than before.
      _agentsCache = (res.data.data?.all_agents || [])
        .sort((a, b) => ((b.online || b.isBot) - (a.online || a.isBot)) || a.fullName.localeCompare(b.fullName));
    }
    if (_agentsFetchPromise === fetchPromise) _agentsFetchPromise = null;
    return _agentsCache || [];
  }).catch(() => {
    if (_agentsFetchPromise === fetchPromise) _agentsFetchPromise = null;
    return [];
  });
  if (!force) _agentsFetchPromise = fetchPromise;
  return fetchPromise;
}

// ── SRM user fetch cache ──────────────────────────────────────────────────────
export function _fetchSRMUsers(force = false) {
  return fetchSrmUsers({ force });
}

// ── Vehicle logo fetch cache ──────────────────────────────────────────────────
let _vehicleLogosCache = null;
let _vehicleLogosFetchPromise = null;
export function _fetchVehicleLogos() {
  if (_vehicleLogosCache) return Promise.resolve(_vehicleLogosCache);
  if (_vehicleLogosFetchPromise) return _vehicleLogosFetchPromise;
  _vehicleLogosFetchPromise = axios.get(
    "https://cdn.jsdelivr.net/gh/avto-dev/vehicle-logotypes@2.x/src/vehicle-logotypes.json"
  ).then(res => {
    _vehicleLogosCache = res.data || {};
    _vehicleLogosFetchPromise = null;
    return _vehicleLogosCache;
  }).catch(() => { _vehicleLogosFetchPromise = null; return {}; });
  return _vehicleLogosFetchPromise;
}
export function getVehicleLogoUrl(brandName) {
  if (!brandName || typeof brandName === "number" || !_vehicleLogosCache) return null;
  const key = String(brandName).toLowerCase().replaceAll(" ", "-");
  return _vehicleLogosCache[key]?.logotype?.uri ?? null;
}

export { loadTemplateChoices as _fetchTemplates } from "../messaging/canonicalCatalogPresentation";

// ── Template helpers ──────────────────────────────────────────────────────────
export function _previewTemplateBody(template, values) {
  return fillMessagePlaceholders(template.bodyOriginal || template.body, { values: Object.fromEntries((template.customParams || []).map((param, i) => [param.paramName, values[i]])) });
}

export function substituteQRText(text, authState, context = {}) {
  return fillMessagePlaceholders(text, { ...context, authState });
}

// ── WATI chat filters (the "All Chats" dropdown) ─────────────────────────────
// get_chat_filters returns WATI's saved filter tabs. Built-in tabs carry
// isDefault: true; custom saved filters (isDefault: false) all share
// filterType 1000, so disambiguating one from another needs its
// filters[0].conditions sent as subConditions (see chatFilterSubConditions).
//
// Custom filters are hidden from the dropdown for now — disambiguating and
// sending their subConditions correctly needs more work on both ends.
// Only built-in (isDefault) tabs show.
const _chatFiltersCache = {};
export async function fetchChatFilters(userId) {
  const ticket = sessionTicket();
  if (!userId) return [];
  if (_chatFiltersCache[userId]) return _chatFiltersCache[userId];
  try {
    const res = await axios.get(
      `${API_DOMAIN}/messaging-v1/provider/chat-filters`,
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    const list = res.data?.success ? (res.data.data ?? []) : [];
    if (!sessionIsCurrent(ticket)) return [];
    const builtInOnly = Array.isArray(list) ? list.filter(f => f.isDefault) : [];
    if (builtInOnly.length > 0) {
      _chatFiltersCache[userId] = builtInOnly;
      return builtInOnly;
    }
  } catch { console.warn('Could not load WhatsApp chat filters; settings remain available.'); }
  return [];
}

let _chatFilterSettingsCache = null;
export async function fetchChatFilterSettings() {
  const ticket = sessionTicket();
  if (_chatFilterSettingsCache) return _chatFilterSettingsCache;
  try {
    const res = await axios.post(
      `${API_DOMAIN}/whatsapp/get_misc_mode`,
      { mode: "chat_settings" },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    const data = res.data?.success ? res.data.data?.chat_filters : null;
    if (!sessionIsCurrent(ticket)) return null;
    _chatFilterSettingsCache = {
      allowed_filters: Array.isArray(data?.allowed_filters) ? data.allowed_filters : [],
      default_filter: data?.default_filter || null,
      // When false (default), admins are exempt from both restrictions
      // above when resolving the available conversation views.
      include_admins: !!data?.include_admins,
    };
  } catch {
    if (!sessionIsCurrent(ticket)) return null;
    _chatFilterSettingsCache = { allowed_filters: [], default_filter: null, include_admins: false };
  }
  return _chatFilterSettingsCache;
}
registerSessionReset(() => {
  Object.keys(_miscOptionsCache).forEach(key => delete _miscOptionsCache[key]);
  Object.keys(_chatFiltersCache).forEach(key => delete _chatFiltersCache[key]);
  _conditionLabelMap = null; _sideLabelMap = null;
  _billingDetailsQuickReplyCache = null; _channelAssignmentsCache = null;
  _regionOptionsCache = null; _fuelTypeOptionsCache = null;
  _agentsRevision++; _agentsCache = null; _agentsFetchPromise = null;
  _chatFilterSettingsCache = null;
});

// ── Highlight a search term inside chat preview text (message-search results) ─
export function highlightMatch(text, term) {
  if (!term || !text) return text;
  const escaped = String(term).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${escaped})`, "gi"));
  if (parts.length <= 1) return text;
  return parts.map((part, i) => (i % 2 === 1 ? <b key={i}>{part}</b> : part));
}

// ── Chat item mapper ──────────────────────────────────────────────────────────
export function expiryDisplay(expiry) {
  if (!expiry?.isValid()) return null;
  const mins = expiry.diff(dayjs(), "minute");
  if (mins <= 0) return { label: "Expired", color: "#ff4d4f" };
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  const label = `${String(hrs).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
  const color = mins < 60 ? "#ff4d4f" : hrs < 6 ? "#fa8c16" : "#8c8c8c";
  return { label, color };
}

// "today at 4:12 PM", "yesterday at …" or "on 03 Oct 2026 at …".
export function windowClosedWhen(closedAt) {
  if (!closedAt?.isValid?.()) return null;
  const time = closedAt.format("h:mm A");
  if (closedAt.isSame(dayjs(), "day")) return `today at ${time}`;
  if (closedAt.isSame(dayjs().subtract(1, "day"), "day")) return `yesterday at ${time}`;
  return `on ${closedAt.format("DD MMM YYYY")} at ${time}`;
}

// ── Past-only date/time pickers ───────────────────────────────────────────────
// Every date filter over chat activity looks backwards — a conversation can't
// have happened in the future — so the pickers cap at "now" rather than
// letting the user pick a range whose tail is guaranteed to be empty. Pair
// both on a picker (disabledDate alone still allows a future *time* today).
const numRange = (start, end) => {
  const out = [];
  for (let i = start; i < end; i++) out.push(i);
  return out;
};

export const disabledFutureDate = (current) => !!current && current.isAfter(dayjs(), "day");

export const disabledFutureTime = (current) => {
  const now = dayjs();
  if (!current || !current.isSame(now, "day")) return {};
  return {
    disabledHours: () => numRange(now.hour() + 1, 24),
    disabledMinutes: (h) => (h === now.hour() ? numRange(now.minute() + 1, 60) : []),
    disabledSeconds: (h, m) => (h === now.hour() && m === now.minute() ? numRange(now.second() + 1, 60) : []),
  };
};

// ── Responsive hook ───────────────────────────────────────────────────────────
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── Sales Pipeline theme ──────────────────────────────────────────────────────
// Dark surfaces share the Global Settings palette; status accents stay semantic.
const pipelineDarkTokens = antdTheme.getDesignToken(appThemeConfig(true));
export function qv4Tokens(isDark) {
  const token = pipelineDarkTokens;
  return isDark ? {
    pageBg: token.colorBgLayout,
    cardBg: token.colorBgContainer,
    headerBg: token.colorBgElevated,
    border: token.colorBorderSecondary,
    text: token.colorText,
    textSub: token.colorTextSecondary,
    input: token.colorBgContainer,
    hover: token.colorFillSecondary,
  } : {
    pageBg: "#eef0f4",
    cardBg: "#ffffff",
    headerBg: "#fafafa",
    border: "#eceef1",
    text: "#262626",
    textSub: "#8c8c8c",
    input: "#ffffff",
    hover: "#f5f5f5",
  };
}

export const Qv4ThemeCtx = React.createContext({ isDark: false, toggle: () => {}, t: qv4Tokens(false) });

export function useQv4Theme() {
  return React.useContext(Qv4ThemeCtx);
}

// Only the Sales Pipeline opts into the global appearance preference today.
export function useQv4ThemeState() {
  const { isDark, toggle } = useAppearance();
  return React.useMemo(() => ({ isDark, toggle, t: qv4Tokens(isDark) }), [isDark, toggle]);
}

// ── Copyable phone component ──────────────────────────────────────────────────
function fallbackCopy(text, onDone) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try { document.execCommand("copy"); onDone?.(); } catch {}
  document.body.removeChild(ta);
}

// maskLast: number of trailing digits a caller wants hideable (0, the
// default, keeps existing behavior — no eye toggle, phone always shown in
// full — so every pre-existing consumer is unaffected).
export function CopyablePhone({ phone, maskLast = 0 }) {
  const [copied, setCopied] = useState(false);
  // Defaults to hidden (masked) when maskLast makes masking possible at all
  // — callers that pass maskLast: 0 (unaffected, canMask is always false for
  // them) never render the toggle and always show the full number.
  const [hidden, setHidden] = useState(true);
  const handleCopy = (e) => {
    e.stopPropagation();
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(phone).then(done).catch(() => fallbackCopy(phone, done));
    } else {
      fallbackCopy(phone, done);
    }
  };
  const canMask = maskLast > 0 && phone?.length > maskLast;
  const display = hidden && canMask ? phone.slice(0, -maskLast) + "•".repeat(maskLast) : phone;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      {display}
      {canMask && (
        <span
          role="button"
          aria-label={hidden ? "Show number" : "Hide last digits"}
          onClick={(e) => { e.stopPropagation(); setHidden(h => !h); }}
          style={{ cursor: "pointer", color: "#8c8c8c" }}
        >
          {hidden ? <EyeInvisibleOutlined style={{ fontSize: 10 }} /> : <EyeOutlined style={{ fontSize: 10 }} />}
        </span>
      )}
      <span role="button" aria-label="Copy number" onClick={handleCopy} style={{ cursor: "pointer", color: copied ? "#52c41a" : "#8c8c8c" }}>
        {copied ? <CheckOutlined style={{ fontSize: 10 }} /> : <CopyOutlined style={{ fontSize: 10 }} />}
      </span>
    </span>
  );
}

// ── Country / international helpers ──────────────────────────────────────────
export function isInternational(q) {
  const phone = ((q.phone || q.whatsappId || "")).replace(/\D/g, "");
  return phone.length > 0 && !phone.startsWith("971") && !phone.startsWith("0");
}

export function getCardCountry(phone, bsuid) {
  const bsuidCode = countryCodeFromBsuid(bsuid);
  if (bsuidCode) return { code: bsuidCode, name: country_codes[bsuidCode] };
  if (!phone) return null;
  try {
    const code = getCountryCode(phone);
    const name = country_codes[code];
    return name ? { code, name } : null;
  } catch { return null; }
}

// ── Agent avatar helpers ──────────────────────────────────────────────────────
// Muted versions of antd's preset palette — each is the original blended 82/18
// with a mid neutral grey, same "matte" treatment as the customer-type avatar
// colors below, so a hash-picked agent color never reads brighter/harder than
// everything else on the card.
export const AGENT_PALETTE = ["#2979e8", "#27b6b6", "#3e5cd8", "#c5871c", "#459922", "#7daa1b", "#b82f7f", "#c56f1e", "#5b2fa3", "#c54522"];

export function getAgentColor(name) {
  if (!name) return "#8c8c8c";
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AGENT_PALETTE[h % AGENT_PALETTE.length];
}

export function agentInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name[0].toUpperCase();
}

// ── Contact / broadcast normalizers ──────────────────────────────────────────
export const RTL_RE = /[ء-يٮٯٱ-ۓەۺ-ۼۿݐ-ݿࢠ-ࣿא-תיִ-ﬨשׁ-זּטּ-לּמּנּסּףּפּצּ-פֿﭐ-﷿ﹰ-ﻼ]/;
export const isRtlText = (text) => {
  if (!text) return false;
  const nonSpace = text.replace(/\s/g, "");
  if (!nonSpace.length) return false;
  const rtlCount = (nonSpace.match(new RegExp(RTL_RE.source, "g")) || []).length;
  // Require ≥20% of non-whitespace chars to be RTL letters — prevents a single
  // Arabic punctuation mark or special char from flipping an English bubble.
  return rtlCount > 0 && rtlCount / nonSpace.length >= 0.2;
};

// ── Address parser ────────────────────────────────────────────────────────────
export function parseAddresses(raw) {
  if (!raw) return [];
  // Handle single- and double-encoded JSON (SQL JSON_OBJECT embeds the
  // addresses column as a string, and legacy rows can be double-stringified)
  let v = raw;
  for (let i = 0; i < 2 && typeof v === "string"; i++) {
    try { v = JSON.parse(v); } catch { return []; }
  }
  return Array.isArray(v) ? v : [];
}

// ── Document icon helper ──────────────────────────────────────────────────────
export function getDocMeta(fileName) {
  const ext = (fileName || "").split(".").pop().toLowerCase();
  const map = {
    pdf:  { icon: FilePdfOutlined,   color: "#ff4d4f" },
    doc:  { icon: FileWordOutlined,  color: "#1677ff" },
    docx: { icon: FileWordOutlined,  color: "#1677ff" },
    xls:  { icon: FileExcelOutlined, color: "#52c41a" },
    xlsx: { icon: FileExcelOutlined, color: "#52c41a" },
    csv:  { icon: FileExcelOutlined, color: "#52c41a" },
    zip:  { icon: FileZipOutlined,   color: "#722ed1" },
    rar:  { icon: FileZipOutlined,   color: "#722ed1" },
    txt:  { icon: FileTextOutlined,  color: "#8696a0" },
    ppt:  { icon: FileOutlined,      color: "#fa8c16" },
    pptx: { icon: FileOutlined,      color: "#fa8c16" },
  };
  const meta = map[ext] || { icon: FileOutlined, color: "#8696a0" };
  return { Icon: meta.icon, color: meta.color, ext: ext.toUpperCase() || "FILE" };
}

// Same icon/color lookup, but from a mimeType instead of a filename — for
// the one moment a filename genuinely can't be shown yet (a document still
// SENDING) where the mimeType is already known and, unlike the name WATI
// eventually assigns, never changes between now and confirmation. Routed
// through WATI_FILE_RULES's own label (already the exact extension string,
// e.g. "PDF", "DOCX") so this stays a single source of truth with the
// upload picker's own accept-list instead of a second hardcoded mapping.
export function getDocMetaFromMime(mimeType) {
  const label = WATI_FILE_RULES[mimeType]?.label;
  return getDocMeta(label ? `x.${label.toLowerCase()}` : "");
}

// WATI's own officially supported document types (from their upload picker,
// same source WATI_FILE_RULES was reconstructed from) — pdf/doc/docx/ppt/
// pptx/xls/xlsx/txt. Anything else (csv, zip, rar, ...) WATI's servers
// silently drop the extension for once uploaded, even though we can often
// still identify the real extension via the customer's original filename.
const WATI_SUPPORTED_DOC_EXTS = Object.values(WATI_FILE_RULES)
  .filter(r => r.category === "document")
  .map(r => r.label.toLowerCase());

function _extractExt(str) {
  if (!str) return null;
  const clean = String(str).split("?")[0].split("/").pop() || "";
  const parts = clean.split(".");
  if (parts.length < 2) return null;
  const ext = parts.pop().toLowerCase();
  return /^[a-z0-9]{1,6}$/.test(ext) ? ext : null;
}

// Reconstructs a usable filename (and whether to warn about it) for an
// incoming WATI document message. WATI's own handling of documents loses
// data two different ways depending on whether the file type is in its
// supported list and whether the customer added a caption — see the two
// real payload shapes this was built against:
//
// Recognized file (extension survives on message.data's storage path):
//   - No caption given → message.text is already the full original filename
//     (WATI passes it through as-is).
//   - Caption given → message.text is the caption; WATI reconstructs the
//     download filename by appending message.data's extension to it, so we
//     mirror that exactly rather than showing something that won't match
//     what the customer/agent actually gets on download.
// Unrecognized file (WATI drops the extension from message.data entirely):
//   - No caption given → message.text is still the full original filename
//     (extension included) — the only data survives via this path.
//   - Caption given → message.text is the caption; there is no way left to
//     recover the real filename or extension at all.
export function resolveDocDisplay(item) {
  // Our own outbound sends already carry a reliable original filename (see
  // the fileName field on the send_*_message payloads) — nothing to
  // reconstruct, and no real caption was ever consumed to build it.
  if (item.fileName) {
    const { Icon, color, ext } = getDocMeta(item.fileName);
    return { fileName: item.fileName, Icon, color, ext, showNotice: false };
  }

  const text = item.text || "";
  const urlExt = _extractExt(item.data);

  if (urlExt) {
    const fileName = text.toLowerCase().endsWith(`.${urlExt}`) ? text : `${text || "Document"}.${urlExt}`;
    const { Icon, color, ext } = getDocMeta(fileName);
    return { fileName, Icon, color, ext, showNotice: false };
  }

  const textExt = _extractExt(text);
  const recognized = textExt && WATI_SUPPORTED_DOC_EXTS.includes(textExt);
  const { Icon, color, ext } = getDocMeta(text);
  return {
    fileName: text || "Unknown file",
    Icon, color, ext,
    // Both "couldn't extract any extension at all" and "extracted one but
    // it's outside WATI's supported list" get the notice — it's WATI's own
    // server-side handling of anything outside that list that's genuinely
    // unreliable, not just whether we can display a name for it.
    showNotice: !recognized,
  };
}

// ── Task card merge ───────────────────────────────────────────────────────────
// Single source of truth for merging a socket task payload onto a board card.
// Fields absent from the payload keep their current card value.
// Columns group by board_stage, so a live move must carry it: take the
// server's value, else derive it from a new status (same rules as the
// backend's query_board_read_model.boardStage).
const STAGE_STATUSES = ["draft", "waiting_for_price", "waiting_customer_confirmation", "confirmed_waiting_payment", "paid", "completed", "cancelled"];
const PARKED_STAGE_STATUSES = ["on_hold", "postponed", "parked", "parked_after_price", "parked_after_confirmation"];
const PAID_STAGE_STATUSES = ["waiting_purchase", "transfer_pending", "transfer_done", "purchased", "purchase_issue", "not_started", "ready_for_delivery", "out_for_delivery", "failed", "rescheduled"];
export function boardStageForStatus(status) {
  if (STAGE_STATUSES.includes(status)) return status;
  if (PARKED_STAGE_STATUSES.includes(status)) return "parked";
  if (status === "delivered") return "completed";
  if (PAID_STAGE_STATUSES.includes(status)) return "paid";
  return null;
}
export function mergeTaskPatch(card, t) {
  const statusChanged = t.status != null && t.status !== card.status;
  return {
    ...card,
    status: t.status ?? card.status,
    board_stage: t.board_stage ?? (statusChanged ? boardStageForStatus(t.status) ?? card.board_stage : card.board_stage),
    financial: t.financial !== undefined ? t.financial : card.financial,
    revision_pending: t.revision_pending ?? card.revision_pending,
    urgency: t.urgency ?? card.urgency,
    vehicle: t.vehicle ?? card.vehicle,
    assigned_user: t.assigned_user ?? card.assigned_user,
    customer: t.customer ?? card.customer,
    query_parts: t.query_parts ?? card.query_parts,
    images: t.images ?? card.images,
    remarks: t.remarks ?? card.remarks,
    total_parts: t.total_parts ?? card.total_parts,
    parts_with_quotes: t.parts_with_quotes ?? card.parts_with_quotes,
    has_after_sales: t.has_after_sales ?? card.has_after_sales,
    after_sales: t.after_sales ?? card.after_sales,
    delivered_at: t.delivered_at ?? card.delivered_at,
    warranty_summary: t.warranty_summary ?? card.warranty_summary,
    postponed_until: t.postponed_until ?? card.postponed_until,
    fulfillment_summary: t.fulfillment_summary ?? card.fulfillment_summary,
  };
}

// ── Date formatters ───────────────────────────────────────────────────────────
export function fmtShortDate(d) {
  return d.format(d.isSame(dayjs(), "year") ? "DD MMM" : "DD MMM YYYY");
}

export function fmtShortDateTime(d) {
  return d.format(d.isSame(dayjs(), "year") ? "DD MMM · hh:mm A" : "DD MMM YYYY · hh:mm A");
}

// ── Pasted image converter ────────────────────────────────────────────────────
export function _pastedImageToJpeg(file) {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => {
        resolve(new File([blob], "pasted-image.jpg", { type: "image/jpeg" }));
      }, "image/jpeg", 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}
